import { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import ThreadList, { type ThreadFilter, type ThreadItem } from "@/components/student/ThreadList";

type ThreadsData = {
  students: Array<{ studentUserId: string; studentName: string }>;
  items: ThreadItem[];
};

const FILTERS: ThreadFilter[] = [
  { id: "all", label: "All", test: () => true },
  { id: "mine", label: "Where you commented", test: (item) => item.viewerPosted },
  { id: "student", label: "Where your student commented", test: (item) => item.studentPosted },
  { id: "feedback", label: "Teacher feedback", test: (item) => item.teacherFeedback },
];

export default function ParentThreads() {
  const [data, setData] = useState<ThreadsData | null>(null);
  const [error, setError] = useState("");
  const [studentId, setStudentId] = useState("");

  useEffect(() => {
    let cancel = false;
    api<{ success: boolean; data: ThreadsData }>("/api/parent/threads")
      .then((env) => {
        if (!cancel) setData(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, []);

  const items = useMemo(
    () => (data?.items || []).filter((item) => !studentId || item.studentUserId === studentId),
    [data, studentId],
  );
  const manyStudents = (data?.students.length || 0) > 1;

  return (
    <div className="student-page student-page--parent">
      <div className="student-page-head">
        <span className="student-eyebrow">Feedback</span>
        <h1>Conversations</h1>
        <p>Where you and your student have commented, and feedback from teachers.</p>
      </div>
      {error ? (
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      ) : !data ? (
        <div aria-busy="true" aria-live="polite">
          <span className="student-vh">Loading threads</span>
          <div className="student-ghost-card">
            <div className="student-ghost student-ghost--line student-ghost--w80" />
            <div className="student-ghost student-ghost--meta" />
          </div>
          <div className="student-ghost-card">
            <div className="student-ghost student-ghost--line student-ghost--w70" />
            <div className="student-ghost student-ghost--meta" />
          </div>
          <div className="student-ghost-card">
            <div className="student-ghost student-ghost--line student-ghost--w60" />
            <div className="student-ghost student-ghost--meta" />
          </div>
        </div>
      ) : (
        <>
          {manyStudents ? (
            <label className="student-course-pick">
              <span className="student-course-pick__label">Student</span>
              <select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                <option value="">All students</option>
                {data.students.map((s) => (
                  <option key={s.studentUserId} value={s.studentUserId}>
                    {s.studentName}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <ThreadList
            items={items}
            filters={FILTERS}
            showStudent={manyStudents && !studentId}
            hrefFor={(item) =>
              `/parent/student/${encodeURIComponent(item.studentUserId || "")}/activity/${encodeURIComponent(item.activityId)}`
            }
            emptyTitle="No conversations yet"
            emptyText="When your student or a teacher writes on an activity, or a teacher leaves feedback, it shows up here."
          />
        </>
      )}
    </div>
  );
}
